import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";

import { AddressAutocomplete } from "../../../components/dashboard/AddressAutocomplete";
import { Modal } from "../../../components/dashboard/Modal";
import { PlaylistAssignmentEditor } from "../../../components/dashboard/PlaylistAssignmentEditor";
import { useConfirm } from "../../../contexts/ConfirmContext";
import { useToast } from "../../../contexts/ToastContext";
import { extractErrorMessage } from "../../../lib/api";
import {
  deleteClient,
  getClientAssignments,
  listClients,
  listPlaylists,
  listScreens,
  saveClient,
  setClientAssignments,
} from "../../../lib/dashboardApi";
import { avatarColor, initials, timeAgo } from "../../../lib/format";
import { hasMinRole } from "../../../lib/hooks/useAuthMe";
import type { Client, PlaylistAssignment, User } from "../../../types/dashboard";

type Ctx = { user?: User };

export function ScreenGroups() {
  const { user } = useOutletContext<Ctx>();
  const canEdit = hasMinRole(user, "admin");
  const qc = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Partial<Client> | null>(null);
  const [saving, setSaving] = useState(false);

  const { data: list = [], isLoading } = useQuery({
    queryKey: ["clients"],
    queryFn: listClients,
  });

  function openCreate() {
    setEditing({});
    setModalOpen(true);
  }
  function openEdit(c: Client) {
    setEditing({ ...c });
    setModalOpen(true);
  }

  async function save() {
    if (!editing?.name?.trim()) {
      toast("Please enter a group name.", "error");
      return;
    }
    setSaving(true);
    try {
      await saveClient(editing);
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast(editing.id ? "Group updated." : "Group created.");
      setModalOpen(false);
    } catch (e) {
      toast(extractErrorMessage(e, "Could not save."), "error");
    } finally {
      setSaving(false);
    }
  }

  async function remove(c: Client) {
    if (
      !(await confirm({
        title: "Delete group?",
        message: `Delete "${c.name}"? Screens in this group will be unassigned (not deleted).`,
        danger: true,
      }))
    )
      return;
    try {
      await deleteClient(c.id);
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast("Deleted.");
      if (selectedId === c.id) setSelectedId(null);
    } catch (e) {
      toast(extractErrorMessage(e, "Could not delete."), "error");
    }
  }

  const selected = selectedId ? list.find((c) => c.id === selectedId) : null;

  if (selected) {
    return (
      <ClientDetail
        client={selected}
        canEdit={canEdit}
        onBack={() => setSelectedId(null)}
        onEdit={() => openEdit(selected)}
        onDelete={() => remove(selected)}
      >
        <ClientModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          editing={editing}
          setEditing={setEditing}
          onSave={save}
          saving={saving}
        />
      </ClientDetail>
    );
  }

  return (
    <>
      <h2 className="page-title">Screen Groups</h2>
      <div className="card">
        <div className="card-header">
          <h3>
            All Screen Groups{" "}
            <span style={{ fontWeight: 400, color: "var(--text2)", fontSize: 13 }}>
              {list.length}
            </span>
          </h3>
          {canEdit && (
            <button type="button" className="btn btn-primary" onClick={openCreate}>
              + Add Group
            </button>
          )}
        </div>
        {isLoading ? (
          <div className="empty">Loading…</div>
        ) : list.length === 0 ? (
          <div className="adv-empty">
            <div className="adv-empty-title">No screen groups yet</div>
            <div className="adv-empty-desc">
              Add a screen group to organize and manage screens and playlists.
            </div>
          </div>
        ) : (
          <div className="adv-grid">
            {list.map((c) => (
              <button
                key={c.id}
                type="button"
                className="adv-card"
                onClick={() => setSelectedId(c.id)}
                style={{ textAlign: "left", fontFamily: "inherit" }}
              >
                <div className="adv-avatar" style={{ background: avatarColor(c.name) }}>
                  {initials(c.name)}
                </div>
                <div className="adv-card-body">
                  <div className="adv-card-name">{c.name}</div>
                  <div className="adv-card-contact">
                    {c.location ?? c.email ?? c.phone ?? "—"}
                  </div>
                  <div className="adv-card-stats">
                    <span>{c.screen_count ?? 0} screens</span>
                    <span>{c.playlist_count ?? 0} playlists</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      <ClientModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        editing={editing}
        setEditing={setEditing}
        onSave={save}
        saving={saving}
      />
    </>
  );
}

function ClientModal({
  open,
  onClose,
  editing,
  setEditing,
  onSave,
  saving,
}: {
  open: boolean;
  onClose: () => void;
  editing: Partial<Client> | null;
  setEditing: (c: Partial<Client> | null) => void;
  onSave: () => void;
  saving: boolean;
}) {
  if (!editing) return null;
  return (
    <Modal open={open} onClose={onClose} width={500} ariaLabel="Screen group form">
      <h3>{editing.id ? "Edit Screen Group" : "Add Screen Group"}</h3>
      <div className="form-group">
        <label>Group Name</label>
        <input
          className="form-input"
          value={editing.name ?? ""}
          onChange={(e) => setEditing({ ...editing, name: e.target.value })}
          placeholder="e.g. Lobby Screens"
          autoFocus
        />
      </div>
      <div className="form-group">
        <label>Address</label>
        <AddressAutocomplete
          value={editing.location ?? ""}
          onChange={(v) => setEditing({ ...editing, location: v })}
        />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Phone</label>
          <input
            className="form-input"
            value={editing.phone ?? ""}
            onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
            type="tel"
          />
        </div>
        <div className="form-group">
          <label>Email</label>
          <input
            className="form-input"
            value={editing.email ?? ""}
            onChange={(e) => setEditing({ ...editing, email: e.target.value })}
            type="email"
          />
        </div>
      </div>
      <div className="form-group">
        <label>Notes</label>
        <textarea
          className="form-input"
          rows={2}
          style={{ resize: "vertical" }}
          value={editing.notes ?? ""}
          onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
        />
      </div>
      <div className="modal-actions">
        <button type="button" className="btn btn-outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" disabled={saving} onClick={onSave}>
          {saving ? "Saving…" : "Save Group"}
        </button>
      </div>
    </Modal>
  );
}

function ClientDetail({
  client,
  canEdit,
  onBack,
  onEdit,
  onDelete,
  children,
}: {
  client: Client;
  canEdit: boolean;
  onBack: () => void;
  onEdit: () => void;
  onDelete: () => void;
  children?: React.ReactNode;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data: playlists = [] } = useQuery({
    queryKey: ["playlists"],
    queryFn: listPlaylists,
  });
  const { data: initialAssignments, refetch } = useQuery({
    queryKey: ["client-assignments", client.id],
    queryFn: () => getClientAssignments(client.id),
  });
  const { data: screensData } = useQuery({
    queryKey: ["screens", { client_id: client.id }],
    queryFn: () => listScreens({ page: 1, per_page: 100, client_id: client.id }),
  });
  const screens = screensData?.screens ?? [];

  const [assignments, setAssignments] = useState<PlaylistAssignment[]>([]);
  useEffect(() => {
    if (initialAssignments) setAssignments(initialAssignments);
  }, [initialAssignments]);
  const [saving, setSaving] = useState(false);

  const partnerPlaylists = playlists.filter((p) => p.is_partner);

  function addPartnerPlaylists() {
    const existing = new Set(assignments.map((a) => a.playlist_id));
    const toAdd = partnerPlaylists
      .filter((p) => !existing.has(p.id))
      .map((p) => ({
        playlist_id: p.id,
        start_time: null,
        end_time: null,
        days_of_week: "0,1,2,3,4,5,6",
      }));
    if (toAdd.length === 0) {
      toast("All partner playlists are already added.");
      return;
    }
    setAssignments([...assignments, ...toAdd]);
    toast(`Added ${toAdd.length} partner playlist${toAdd.length === 1 ? "" : "s"}. Review and save.`);
  }

  async function save() {
    setSaving(true);
    try {
      await setClientAssignments(client.id, assignments);
      qc.invalidateQueries({ queryKey: ["client-assignments", client.id] });
      qc.invalidateQueries({ queryKey: ["screens"] });
      refetch();
      toast("Group playlists saved.");
    } catch (e) {
      toast(extractErrorMessage(e, "Could not save."), "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="card">
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <button type="button" className="btn btn-outline" onClick={onBack}>
            ← All Groups
          </button>
          <span style={{ flex: 1 }} />
          {canEdit && (
            <>
              <button type="button" className="btn-action-edit" onClick={onEdit}>
                Edit
              </button>
              <button type="button" className="btn-action-delete" onClick={onDelete}>
                Delete
              </button>
            </>
          )}
        </div>
        <div className="adv-detail-header">
          <div className="adv-avatar" style={{ background: avatarColor(client.name) }}>
            {initials(client.name)}
          </div>
          <div className="adv-detail-info">
            <h3>{client.name}</h3>
            <div className="adv-detail-meta">
              {client.location && <span>📍 {client.location}</span>}
              {client.email && <span>✉ {client.email}</span>}
              {client.phone && <span>☎ {client.phone}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Group Playlists</h3>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ color: "var(--text2)", fontSize: 12 }}>
              Inherited by all screens under this group
            </span>
            {canEdit && partnerPlaylists.length > 0 && (
              <button
                type="button"
                className="btn btn-sm btn-outline"
                onClick={addPartnerPlaylists}
              >
                + Add Partner Playlists
              </button>
            )}
          </div>
        </div>
        <PlaylistAssignmentEditor
          assignments={assignments}
          playlists={playlists}
          onChange={setAssignments}
          readOnly={!canEdit}
        />
        {canEdit && (
          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={save}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save Group Playlists"}
            </button>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Pi Screens</h3>
        </div>
        {screens.length === 0 ? (
          <div className="empty">No screens in this group.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>IP / Hostname</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {screens.map((s) => (
                <tr key={s.id}>
                  <td>
                    <strong>{s.name}</strong>
                  </td>
                  <td>
                    <span className={s.is_online ? "badge badge-online" : "badge badge-offline"}>
                      {s.is_online ? "Online" : "Offline"}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                      {s.ip_address ?? "-"}
                      {s.hostname ? ` / ${s.hostname}` : ""}
                    </span>
                  </td>
                  <td>
                    <span style={{ color: "var(--text2)", fontSize: 12 }}>
                      {timeAgo(s.last_seen)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {children}
    </>
  );
}
